import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCollection } from '../hooks/useFirestore'
import { getFileTypeInfo } from '../lib/fileIcons'
import './LorePage.css'

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'history', label: 'History' },
  { id: 'locations', label: 'Locations' },
  { id: 'factions', label: 'Factions' },
  { id: 'deities', label: 'Deities' },
  { id: 'items', label: 'Items' },
  { id: 'misc', label: 'Miscellaneous' }
]

function formatHarptosDate(date) {
  if (!date) return null
  return `${date.day || 1} ${date.month}, ${date.year} DR`
}

export default function LorePage() {
  const { data: articles, loading } = useCollection('lore', { orderByField: 'sortOrder' })
  const { data: handouts, loading: handoutsLoading } = useCollection('handouts', { orderByField: 'sortOrder' })
  const [activeCategory, setActiveCategory] = useState('all')
  const [handoutYearFilter, setHandoutYearFilter] = useState(null)

  // Filter out hidden items from public view
  const visibleArticles = articles.filter(a => a.hidden !== true)
  const visibleHandouts = handouts.filter(h => h.hidden !== true)

  // Get available years from visible handouts
  const handoutYears = [...new Set(
    visibleHandouts
      .filter(h => h.receivedDate?.year)
      .map(h => h.receivedDate.year)
  )].sort((a, b) => a - b)

  // Filter handouts by year
  const filteredHandouts = handoutYearFilter
    ? visibleHandouts.filter(h => h.receivedDate?.year === handoutYearFilter)
    : visibleHandouts

  const filteredArticles = activeCategory === 'all'
    ? visibleArticles
    : visibleArticles.filter(article => article.category === activeCategory)

  if (loading || handoutsLoading) {
    return (
      <div className="container">
        <div className="lore-loading">
          <div className="loading-spinner" />
          <p>Loading lore...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="lore-header">
        <h1>Lore</h1>
        <p className="text-secondary">Explore the history, locations, and legends of our world.</p>
      </div>

      {visibleArticles.length > 0 && (
        <div className="lore-categories">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {visibleArticles.length === 0 ? (
        <div className="lore-empty">
          <p>No lore articles yet.</p>
          <p className="text-secondary">Check back soon for world-building content.</p>
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="lore-empty">
          <p>No articles in this category.</p>
        </div>
      ) : (
        <div className="lore-grid">
          {filteredArticles.map(article => (
            <Link
              key={article.id}
              to={`/lore/${article.slug || article.id}`}
              className="lore-card"
            >
              {article.coverImageUrl && (
                <div className="lore-card-image">
                  <img src={article.coverImageUrl} alt={article.title} />
                </div>
              )}
              <div className="lore-card-content">
                <span className="lore-card-category">{article.category || 'misc'}</span>
                <h3>{article.title}</h3>
                {article.excerpt && (
                  <p className="lore-card-excerpt">{article.excerpt}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Handouts Section */}
      <div className="handouts-section">
        <div className="handouts-header">
          <div>
            <h2>Handouts</h2>
            <p className="text-secondary">Documents and materials from our adventures.</p>
          </div>

          {handoutYears.length > 0 && (
            <div className="handout-filters">
              <select
                value={handoutYearFilter || ''}
                onChange={(e) => setHandoutYearFilter(e.target.value ? parseInt(e.target.value) : null)}
                className="year-filter"
              >
                <option value="">All Years</option>
                {handoutYears.map(year => (
                  <option key={year} value={year}>{year} DR</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {visibleHandouts.length === 0 ? (
          <div className="lore-empty">
            <p>No handouts yet.</p>
            <p className="text-secondary">Documents will appear here as they&apos;re discovered.</p>
          </div>
        ) : filteredHandouts.length === 0 ? (
          <div className="lore-empty">
            <p>No handouts from {handoutYearFilter} DR.</p>
          </div>
        ) : (
          <div className="handouts-grid">
            {filteredHandouts.map(handout => (
              <a
                key={handout.id}
                href={handout.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="handout-card"
              >
                {handout.thumbnailUrl ? (
                  <div className="handout-thumbnail">
                    <img src={handout.thumbnailUrl} alt={handout.title} />
                  </div>
                ) : (
                  <div
                    className="handout-icon"
                    style={{ background: getFileTypeInfo(handout.fileUrl).color + '22' }}
                  >
                    <span className="file-type-emoji">{getFileTypeInfo(handout.fileUrl).icon}</span>
                  </div>
                )}
                <div className="handout-content">
                  {handout.receivedDate && (
                    <span className="handout-date">{formatHarptosDate(handout.receivedDate)}</span>
                  )}
                  <h3>{handout.title}</h3>
                  {handout.description && (
                    <p className="handout-description">{handout.description}</p>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
